import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  // Slider,
  // SliderFilledTrack,
  // SliderMark,
  // SliderThumb,
  // SliderTrack,
  Text,
  // Tooltip,
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import getLatLong from "../../../../GlobalActions/getGeoCode";
import { ShowAlert } from "../../../../GlobalComponents/ShowAlert/ShowAlert";
import { updateBeneficiaryAddress, queryHospitals } from "../../../../http";
import { useNavigate } from "react-router-dom";
import FindHospital from "./FIndHospital";
import { Address } from "../../../../Helper/BenInterface";
import { GrFormPrevious } from "react-icons/gr";
import { FaSearch } from "react-icons/fa";
import debounce from "lodash/debounce";
interface PreferredHospitalProps {
  changeStep: (step: number) => void;
  setData: (data: any) => void;
  data: any;
  setCurrentStep: (step: number) => void;
}

export const PreferredHospital: React.FC<PreferredHospitalProps> = ({
  changeStep,
  setData,
  data,
  setCurrentStep,
}) => {
  const [selectedAddress, setSelectedAddress] = useState(data.address[0] || null);
  const [hospitalList, setHospitalList] = useState<any[]>([]);
  const [searchText, setSearchText] = useState("");
  const [isEsic, setIsEsic] = useState(false);
  const [geoCode, setGeoCode] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddConfirmPopup, setShowAddConfirmPopup] = useState(false);
  const [preferredHospital, setPreferredHospital] = useState(
    data.address[0]?.preferredHospital?.[0] || null
  );
  const [saving, setSaving] = useState(false);
  const [isValidGeoCode, setIsValidGeoCode] = useState(true);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  // Fetch geoCode when address changes
  useEffect(() => {
    if (!selectedAddress) return;
    const fetchGeo = async () => {
      const addressLine1: string = selectedAddress.addressLLine1 && typeof selectedAddress.addressLine1 === "string" ? selectedAddress.addressLine1.trim() : "";
      const addressLine2: string = selectedAddress.addressLine2 && typeof selectedAddress.addressLine2 === "string" ? selectedAddress.addressLine2.trim() : "";
      const city: string = selectedAddress.city && typeof selectedAddress.city === "string" ? selectedAddress.city.trim() : "";
      const pinCode: number = typeof selectedAddress.pinCode === "number" ? selectedAddress.pinCode : 0;
      let geo = {
        lat: selectedAddress?.location?.[0]?.latitude || selectedAddress?.location?.latitude,
        lng: selectedAddress?.location?.[0]?.longitude || selectedAddress?.location?.longitude,
      };
      if ((!geo.lat || !geo.lng) && addressLine1 && city && pinCode) {
        const addressText = `${addressLine1} ${addressLine2} ${city} ${pinCode}`;
        const resp = await getLatLong(addressText);
        if (resp?.lat && resp?.lng) geo = resp;
      }
      setGeoCode(geo.lat && geo.lng ? geo : null);
      setIsValidGeoCode(isValidGeo(geo.lat && geo.lng ? geo : null))
    };
    fetchGeo();
    setPreferredHospital(selectedAddress?.preferredHospital?.[0] || null);
  }, [selectedAddress]);

  // Debounced hospital fetch with lodash debounce and cancellation
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchHospitals = React.useCallback(
    async (searchTextVal: string, geoCodeVal: any, selectedAddressVal: any) => {
      if (!geoCodeVal) {
        setHospitalList([]);
        return;
      }
      setIsLoading(true);
      // Cancel previous request if any
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;
      try {
        const res = await queryHospitals({
          name: searchTextVal,
          PickupLatitude: geoCodeVal.lat,
          PickupLongitude: geoCodeVal.lng,
          ...(isEsic && { isEsic: true }),
        }, controller);
        let hospitals: any[] = res?.data?.data || [];
        const savedPreferred = selectedAddressVal?.preferredHospital?.[0];
        hospitals = hospitals.sort((a: any, b: any) => {
          const distA = parseFloat(a?.distance?.toFixed?.(2)) || 0;
          const distB = parseFloat(b?.distance?.toFixed?.(2)) || 0;

          // Priority 1: Preferred hospital
          if (savedPreferred && a._id === savedPreferred.id) return -1;
          if (savedPreferred && b._id === savedPreferred.id) return 1;

          // Priority 2: Emergency handling, but only if distance ≤ 25
          const aIsEmergencyPreferred = a.handleEmergency && distA <= 25;
          const bIsEmergencyPreferred = b.handleEmergency && distB <= 25;

          if (aIsEmergencyPreferred && !bIsEmergencyPreferred) return -1;
          if (!aIsEmergencyPreferred && bIsEmergencyPreferred) return 1;

          // Fallback: maintain order
          return 0;
        });

        setHospitalList(hospitals);
      } catch (err: any) {
        if (err.name === 'CanceledError') {
          // Request was cancelled, do nothing
        } else {
          setHospitalList([]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [isEsic]
  );

  // Debounced version of fetchHospitals
  const debouncedFetchHospitals = React.useMemo(
    () => debounce(fetchHospitals, 500),
    [fetchHospitals]
  );

  useEffect(() => {
    debouncedFetchHospitals(searchText, geoCode, selectedAddress);
    return () => {
      debouncedFetchHospitals.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [searchText, geoCode, selectedAddress, isEsic, debouncedFetchHospitals]);

  // Handlers
  const handleAddressSelect = (address: any) => {
    setSelectedAddress(address);
  };
  const handleHospitalSelect = (hospital: any) => {
    setPreferredHospital({
      id: hospital._id,
      hospitalName: hospital.name,
      distance: hospital.distance?.toFixed(2) || "N/A",
    });
  };
  const handleSavePreferred = async () => {
    if (!preferredHospital) {
      ShowAlert("warning", "Please select a hospital");
      return;
    }
    setSaving(true);
    try {
      await updateBeneficiaryAddress(data._id, {
        _id: selectedAddress._id,
        preferredHospital: [preferredHospital],
      });
      ShowAlert("success", "Preferred Hospital Saved Successfully!");
      // Update selectedAddress.preferredHospital so the effect will re-sort and re-fetch
      setSelectedAddress({ ...selectedAddress, preferredHospital: [preferredHospital] });
      setTimeout(() => navigate(`/profile/view/${data._id}`), 1500);
    } catch (error) {
      ShowAlert("warning", "Something went wrong, please try again later");
    } finally {
      setSaving(false);
    }
  };
  const handleAddHospital = () => setShowAddConfirmPopup(true);
  const handleConfirmAdd = () => {
    setShowAddConfirmPopup(false);
    setShowAddModal(true);
  };
  const handleCloseModal = () => setShowAddModal(false);

  const isValidGeo = (geo: any) => {
    return (
      geo &&
      typeof geo.lat === "number" &&
      typeof geo.lng === "number" &&
      !isNaN(geo.lat) &&
      !isNaN(geo.lng) &&
      geo.lat >= -90 && geo.lat <= 90 &&
      geo.lng >= -180 && geo.lng <= 180
    );
  };

  return (
    <>
      {data.address.length < 1 ? (
        <>
          <Heading color={"red"}>Please add address first!</Heading>
        </>
      ) : (
        <>
          <></>
          {/* @ts-ignore */}
          <>
            <Heading textAlign={"center"} mb={"3"}>Choose address to set preferred hospital</Heading>
            <Flex wrap={"wrap"} direction="row" gap={"10"} mb={"10"} mt={"10"} >
              {data.address.length > 0 &&
                data.address.map((address: Address, index: number) => {
                  return (
                    <SimpleGrid
                      width="100%" columns={{ base: 1, md: 2 }}
                      padding={"10px"}
                      spacing={2} border="1px solid #cecece"
                      borderRadius="10"
                    >
                      <Box width="100%">
                        <Checkbox
                          defaultChecked={true}
                          key={address._id}
                          isChecked={selectedAddress === address}
                          onChange={() => {
                            handleAddressSelect(address);
                            setPreferredHospital(
                              address.preferredHospital?.[0]
                                ? address.preferredHospital[0]
                                : null
                            );
                          }}
                          // border="1px solid #cecece"
                          p={3}
                          borderRadius="10"
                          width={"100%"}
                        // flex={3}
                        >
                          <Text fontSize="sm" color="gray.600" fontWeight="medium">
                            Address Label : {address?.label || "N/A"}
                          </Text>
                          {address?.addressLine1} <br />
                          {address?.city}, {address.pinCode}
                          <br />
                        </Checkbox>
                      </Box>
                      <Box marginLeft={{ base: "25px" }}>
                        {address?.preferredHospital &&
                          address.preferredHospital.length > 0 ? (
                          <Box
                            width="100%"
                            // border="1px solid #cecece"
                            p={3}
                            borderRadius="10"
                          // flex={2}
                          >
                            <Text fontSize="sm" color="gray.600" fontWeight="medium">
                              Current Preferred Hospital:
                            </Text>
                            <Text>
                              {address.preferredHospital[0]?.hospitalName || ""}
                            </Text>
                          </Box>
                        ) : <Box
                          width="100%"
                          // border="1px solid #cecece"
                          p={3}
                          borderRadius="10"
                        // flex={2}
                        >
                          <Text fontSize="sm" color="gray.600" fontWeight="medium">
                            Current Preferred Hospital:
                          </Text>
                          <Text color={"red.300"}>
                            Please add preferred hospital for this address
                          </Text>
                        </Box>}
                      </Box>
                    </SimpleGrid>
                  );
                })}
            </Flex>
          </>
          {/* <Box
            p={"5"}
            border={"1px solid #cecece"}
            mb={"5"}
            borderRadius={"10"}
          >
            <Text fontWeight={"1000"} mb={"3"}>
              Increase Search Radius
            </Text>
            <Slider
              id="slider"
              defaultValue={5}
              min={5}
              max={50}
              colorScheme="teal"
              onChange={(v) => handleSliderValue(v)}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <SliderMark value={5} mt="1" ml="-2.5" fontSize="sm">
                5 KM
              </SliderMark>
              <SliderMark value={10} mt="1" ml="-2.5" fontSize="sm">
                10 KM
              </SliderMark>
              <SliderMark value={20} mt="1" ml="-2.5" fontSize="sm">
                20 KM
              </SliderMark>
              <SliderMark value={30} mt="1" ml="-2.5" fontSize="sm">
                30 KM
              </SliderMark>
              <SliderMark value={40} mt="1" ml="-2.5" fontSize="sm">
                40 KM
              </SliderMark>
              <SliderTrack height={"2"}>
                <SliderFilledTrack />
              </SliderTrack>
              <Tooltip
                hasArrow
                bg="teal.500"
                color="white"
                placement="top"
                isOpen={showTooltip}
                label={`${sliderValue} KM`}
              >
                <SliderThumb borderWidth={"5px"} bg={"teal"} />
              </Tooltip>
            </Slider>
          </Box> */}

          {isValidGeoCode ?
            <>
              <Box
                border={"1px solid #cecece"}
                mb={"10"}
                p={"5"}
                borderRadius={"10"}
              >
                <Heading textAlign={"center"} mb={"5"}>Select Nearby Hospital</Heading>

                <Flex direction={{ base: "column", md: "row" }} gap={"15px"} mb={"20px"} alignItems={{ base: "stretch", md: "center" }}>
                  <Box
                    as="label"
                    display="flex"
                    alignItems="center"
                    gap="8px"
                    cursor="pointer"
                    px={4}
                    py={2}
                    borderRadius="md"
                    border="1px solid"
                    borderColor={isEsic ? "blue.500" : "gray.300"}
                    bg={isEsic ? "blue.50" : "white"}
                    transition="all 0.2s"
                    _hover={{ borderColor: "blue.400", bg: isEsic ? "blue.100" : "gray.50" }}
                    minW={{ base: "auto", md: "180px" }}
                    justifyContent="center"
                  >
                    <Checkbox
                      isChecked={isEsic}
                      onChange={(e) => setIsEsic(e.target.checked)}
                      colorScheme="blue"
                    />
                    <Text fontWeight="medium" fontSize="sm" color={isEsic ? "blue.700" : "gray.700"}>
                      ESIC Hospitals
                    </Text>
                  </Box>

                  <InputGroup flex={1}>
                    <InputLeftElement pointerEvents="none">
                      <FaSearch color="gray.300" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search for hospital by name"
                      value={searchText}
                      onChange={(e: any) => setSearchText(e.target.value)}
                    />
                  </InputGroup>
                </Flex>
                {/* } */}
                {isLoading ? (
                  <Heading textAlign={"center"}>Loading...</Heading>
                ) : hospitalList.length < 1 ? (
                  <Flex justifyContent={"center"} flexDir={"column"} gap={"20px"}>
                    <Heading color={"red"} textAlign={"center"}>Hospital Not Found!</Heading>
                    <Heading size={"md"} color={"red"} textAlign={"center"}>If no hospital found click add hospital to add your hospital</Heading>
                  </Flex>
                ) : (
                  <>
                    <Box height={"48vh"} overflowY={"scroll"} p={"5"}>
                      <Heading textAlign={"center"} mb={"20px"} size={"md"}>Below are the {hospitalList?.length || 50} nearest hospitals. Use the search to refine the results.</Heading>
                      <Grid
                        templateColumns={[
                          "repeat(1, 1fr)",
                          "repeat(2, 1fr)",
                          "repeat(3, 1fr)",
                        ]}
                        gap={[4]}
                      >
                        {hospitalList.map((hospital: any, index: number) => {

                          return (
                            <Checkbox
                              border="1px solid #cecece"
                              p={4}
                              borderRadius="10"
                              height="auto"
                              key={hospital._id}
                              onChange={() => handleHospitalSelect(hospital)}
                              isChecked={
                                preferredHospital && preferredHospital.id === hospital._id
                              }
                              sx={{
                                width: "100%",
                                whiteSpace: "normal",
                                overflow: "hidden",
                                position: "relative",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                              }}
                            >


                              {hospital?.handleEmergency && (
                                <Box
                                  title="Emergency Hospital"
                                  style={{
                                    width: '2.2rem',
                                    height: '2.2rem',
                                    borderRadius: '50%',
                                    backgroundColor: '#E1E4FC',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'absolute',
                                    right: '5px',
                                    top: '5px',
                                    flexShrink: 0,
                                  }}
                                >
                                  <Text
                                    style={{
                                      color: 'white',
                                      backgroundColor: '#F0389C',
                                      padding: '.25rem',
                                      borderRadius: '.3rem',
                                      fontWeight: 'bold',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    E
                                  </Text>
                                </Box>
                              )}

                              {hospital?.isGovernmentHospital && (
                                <Box
                                  title="Government Hospital"
                                  style={{
                                    width: '2.2rem',
                                    height: '2.2rem',
                                    borderRadius: '50%',
                                    backgroundColor: '#E8F5E8',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'absolute',
                                    right: hospital?.handleEmergency ? '45px' : '5px',
                                    top: '5px',
                                    flexShrink: 0,
                                  }}
                                >
                                  <Text
                                    style={{
                                      color: 'white',
                                      backgroundColor: '#38a169',
                                      padding: '.25rem',
                                      borderRadius: '.3rem',
                                      fontWeight: 'bold',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    G
                                  </Text>
                                </Box>
                              )}

                              {hospital?.isEsic && (
                                <Box
                                  title="ESIC Hospital"
                                  style={{
                                    width: '2.2rem',
                                    height: '2.2rem',
                                    borderRadius: '50%',
                                    backgroundColor: '#FFF4E6',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'absolute',
                                    right: (hospital?.handleEmergency && hospital?.isGovernmentHospital) ? '90px' : (hospital?.handleEmergency || hospital?.isGovernmentHospital) ? '50px' : '5px',
                                    top: '5px',
                                    flexShrink: 0,
                                  }}
                                >
                                  <Text
                                    style={{
                                      color: '#ffffff',
                                      backgroundColor: '#FF6B35',
                                      padding: '3px 8px',
                                      borderRadius: '6px',
                                      fontWeight: '800',
                                      fontSize: '0.65rem',
                                      letterSpacing: '0.8px',
                                      textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                                      boxShadow: '0 2px 5px rgba(255, 107, 53, 0.35)',
                                      lineHeight: '1',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    ESIC
                                  </Text>
                                </Box>
                              )}

                              {/* Hospital Info */}
                              <Box mt={2} width="100%" wordBreak="break-word">
                                <Text fontWeight="bold" noOfLines={1}>
                                  {hospital.name}
                                </Text>

                                <Text mt={1} fontSize="sm" color="gray.600" whiteSpace="pre-wrap">
                                  {hospital.address}
                                </Text>

                                <Flex gap={"5px"} mt={2} fontSize="sm">
                                  <Text fontWeight={1000}>Distance: </Text>
                                  <Text fontWeight={1000} color={hospital?.distance?.toFixed(2) > 25 ? "red" : "none"}>{hospital?.distance?.toFixed(2)} KM</Text>
                                </Flex>
                              </Box>
                              {preferredHospital && preferredHospital.id === hospital._id && (
                                <Button
                                  isLoading={saving}
                                  padding={"10px"}
                                  width="100%"
                                  bgColor="#38a169"
                                  color="white"
                                  mt="10px"
                                  onClick={handleSavePreferred}
                                  whiteSpace="normal" // 👈 key for wrapping text
                                >
                                  Save Preferred Hospital
                                </Button>
                              )}
                            </Checkbox>
                          );
                        })}
                      </Grid>
                    </Box>
                  </>
                )}
              </Box>
              <Box width={"80%"} margin={"auto"} textAlign={"center"}>
                <Heading as={"h2"} size={"md"} mb={"5"}>
                  Hospital Not Found?
                </Heading>
                <Button color="white" bg="#3182ce" onClick={handleAddHospital}>
                  Add Hospital
                </Button>
              </Box>
            </> : <Box width={"80%"} margin={"auto"} textAlign={"center"}>
              <Heading as={"h2"} size={"md"} mb={"5"}>
                Update your exact address location to proceed with hospital selection
              </Heading>
              <Button
                onClick={() => {
                  // Store the address to edit and redirect info in sessionStorage
                  sessionStorage.setItem('editAddressId', selectedAddress._id);
                  sessionStorage.setItem('redirectAfterEdit', '6'); // Step 6 is PreferredHospital
                  setCurrentStep(2);
                }}
                colorScheme="blue"
                size="md"
                mt={"3"}
              >
                Edit Address
              </Button>
            </Box>
          }
        </>
      )}

      <AlertDialog
        isOpen={showAddConfirmPopup}
        leastDestructiveRef={cancelRef}
        onClose={() => setShowAddConfirmPopup(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Check if hospital already exists?
            </AlertDialogHeader>

            <AlertDialogBody>
              Cannot find your desired Hospital, Please search and add
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setShowAddConfirmPopup(false)}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={handleConfirmAdd} ml={3}>
                Add
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* add hospital modal */}

      {showAddModal && (
        <FindHospital
          data={data}
          addresses={data.address}
          closeModal={handleCloseModal}
          setData={setData}
          selectedAddress={selectedAddress}
        />
      )}
      <GrFormPrevious
        style={{
          fontSize: "40px",
          padding: "10px",
          borderRadius: "50px",
          backgroundColor: "#cecece",
          color: "black",
          cursor: "pointer",
          boxShadow: "rgba(0, 0, 0, 0.15) 0px 2px 8px",
        }}
        onClick={() => changeStep(-1)}
      />
    </>
  );
};
